// SaveServidor.tsx
import { Servidor, ServidorForm } from "../../types";
import { useForm } from "react-hook-form";
import { ROUTER_PATHS } from "@/shared";
import { useNavigate } from "react-router-dom";
import {
  CustomTextField,
  SingleFormBoxScene,
  CustomAutocomplete,
} from "@/shared/components/ui";
import { useEffect, useState } from "react";
import { useFetchPostServidor, useFetchPutServidor } from "@/shared/actions";
import { servidorSchema } from "../../utils";
import { yupResolver } from "@hookform/resolvers/yup";

type SaveServidorProps = {
  titulo: string;
  data?: Servidor | null;
};

type Opcion = { value: string; label: string };

// Opciones de los enums del backend
const tipoServidorOptions: Opcion[] = [
  { value: "FISICO", label: "Físico" },
  { value: "VIRTUAL", label: "Virtual" },
];
const ambienteOptions: Opcion[] = [
  { value: "PRODUCCION", label: "Producción" },
  { value: "DESARROLLO", label: "Desarrollo" },
];
const tecnologiaOptions: Opcion[] = [
  { value: "DOCKER", label: "Docker" },
  { value: "KUBERNETES", label: "Kubernetes" },
  { value: "ON_SERVER", label: "On Server" },
  { value: "PROXMOX", label: "Proxmox" },
];
const categoriaOptions: Opcion[] = [
  { value: "SERVIDOR", label: "Servidor" },
  { value: "SOFTNET", label: "Softnet" },
  { value: "NOC", label: "NOC" },
  { value: "EVA", label: "EVA" },
  { value: "FIBRAMAX", label: "Fibramax" },
];

const sistemaOperativoOptions: Opcion[] = [
  { value: "WINDOWS", label: "Windows" },
  { value: "MACOS", label: "MacOs" },
  { value: "LINUX", label: "Linux" },
];

export default function SaveServidor({ titulo, data }: SaveServidorProps) {
  const returnUrl = ROUTER_PATHS.control_cambios.servidorNav;
  const navigate = useNavigate();
  const [shrink, setShrink] = useState(false);

  const createServidor = useFetchPostServidor({
    navigate,
    returnUrl,
  });
  const updateServidor = useFetchPutServidor(navigate, returnUrl);

  const form = useForm<ServidorForm>({
    resolver: yupResolver(servidorSchema) as any,
    mode: "onChange",
    defaultValues: {
      tipoServidor: "",
      nombreServidor: "",
      ubicacionServidor: "",
      subId: 0,
      servidorVirtual: "",
      ipServidor: "",
      ambiente: "",
      sistemaOperativo: "",
      tecnologiaServidor: "",
      categoria: "",
      descripcionServidor: "",
      estado: true,
      estadoCritico: false,
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = form;

  const onSave = () => {
    if (!isValid) return;
    const values = { ...form.getValues() };
    if (data?.servidorId) {
      return updateServidor.mutate({
        id: data.servidorId,
        data: values as any,
      });
    }
    return createServidor.mutate(values);
  };

  useEffect(() => {
    if (data) {
      setShrink(true);
      reset({ ...data });
    }
  }, [data]);

  return (
    <SingleFormBoxScene
      titlePage={titulo}
      onCancel={() => navigate(returnUrl)}
      onSave={handleSubmit(onSave)}
    >
      <CustomAutocomplete<Opcion>
        label="Tipo de servidor"
        name="tipoServidor"
        control={form.control}
        options={tipoServidorOptions}
        valueKey="label"
        actualValueKey="value"
        isLoadingData={false}
        defaultValue={form.getValues().tipoServidor ?? ""}
        error={errors.tipoServidor}
        helperText={errors.tipoServidor?.message ?? ""}
      />
      <CustomTextField
        label="Nombre del servidor"
        name="nombreServidor"
        control={form.control}
        defaultValue={form.getValues().nombreServidor || ""}
        error={errors.nombreServidor}
        helperText={errors.nombreServidor?.message ?? ""}
        shrink={shrink}
      />
      <CustomTextField
        label="Ubicación del servidor"
        name="ubicacionServidor"
        control={form.control}
        defaultValue={form.getValues().ubicacionServidor || ""}
        error={errors.ubicacionServidor}
        helperText={errors.ubicacionServidor?.message ?? ""}
        shrink={shrink}
      />
      <CustomTextField
        label="Sub id"
        name="subId"
        type={"number"}
        control={form.control}
        defaultValue={form.getValues().subId || ""}
        error={errors.subId}
        helperText={errors.subId?.message ?? ""}
        shrink={shrink}
      />
      <CustomTextField
        label="Servidor virtual"
        name="servidorVirtual"
        control={form.control}
        defaultValue={form.getValues().servidorVirtual || ""}
        error={errors.servidorVirtual}
        helperText={errors.servidorVirtual?.message ?? ""}
        shrink={shrink}
      />
      <CustomTextField
        label="IP del servidor"
        name="ipServidor"
        control={form.control}
        defaultValue={form.getValues().ipServidor || ""}
        error={errors.ipServidor}
        helperText={errors.ipServidor?.message ?? ""}
        shrink={shrink}
      />
      <CustomAutocomplete<Opcion>
        label="Ambiente"
        name="ambiente"
        control={form.control}
        options={ambienteOptions}
        valueKey="label"
        actualValueKey="value"
        isLoadingData={false}
        defaultValue={form.getValues().ambiente ?? ""}
        error={errors.ambiente}
        helperText={errors.ambiente?.message ?? ""}
      />
      <CustomAutocomplete<Opcion>
        label="Sistema operativo"
        name="sistemaOperativo"
        control={form.control}
        options={sistemaOperativoOptions}
        valueKey="label"
        actualValueKey="value"
        isLoadingData={false}
        defaultValue={form.getValues().sistemaOperativo ?? ""}
        error={errors.sistemaOperativo}
        helperText={errors.sistemaOperativo?.message ?? ""}
      />
      <CustomAutocomplete<Opcion>
        label="Tecnología del servidor"
        name="tecnologiaServidor"
        control={form.control}
        options={tecnologiaOptions}
        valueKey="label"
        actualValueKey="value"
        isLoadingData={false}
        defaultValue={form.getValues().tecnologiaServidor ?? ""}
        error={errors.tecnologiaServidor}
        helperText={errors.tecnologiaServidor?.message ?? ""}
      />
      <CustomAutocomplete<Opcion>
        label="Categoría"
        name="categoria"
        control={form.control}
        options={categoriaOptions}
        valueKey="label"
        actualValueKey="value"
        isLoadingData={false}
        defaultValue={form.getValues().categoria ?? ""}
        error={errors.categoria}
        helperText={errors.categoria?.message ?? ""}
      />
      <CustomTextField
        label="Descripción"
        name="descripcionServidor"
        control={form.control}
        defaultValue={form.getValues().descripcionServidor || ""}
        error={errors.descripcionServidor}
        helperText={errors.descripcionServidor?.message ?? ""}
        shrink={shrink}
      />
    </SingleFormBoxScene>
  );
}
